import { create } from "zustand";
import { userApi } from "./api";
import { useSession } from "./session";

/**
 * THE DAILY WALL ALLOWANCE, client side — the meter the screen shrinks by.
 *
 * WALL-MINUTES, NOT MINUTES. A tile playing for a minute is one wall-minute; sixteen of
 * them for a minute is sixteen. So fewer tiles last longer and a loop costs the same as
 * anything else — the trade the owner asked for, legible on the gauge as "sixteen tiles
 * for an hour, eight for two, four for four".
 *
 * THE SCREEN COUNTS, THE LEDGER CORRECTS. Only the screen can shrink itself, so the
 * meter is enforced here whatever the server says; and only the screen knows moment to
 * moment how many tiles are playing. It ticks once a second (DisplayApp drives it with
 * the count of tiles it is drawing on a view that counts, or zero when paused / on a
 * wall / on a stage). Signed in, the server's ledger — the SUM of the day's screen
 * watch intervals, walls excluded (db.wallSecondsSince) — seeds the number on mount and
 * re-syncs it on every watch batch, so a second screen or a reload lands on the same
 * figure; between syncs the local count carries it. Anonymous, there is no ledger: the
 * day's count lives in localStorage, which a fresh browser resets — accepted, because
 * anonymous use is shallow (median goon session under a minute) and an account is the
 * first wall anyway.
 *
 * ONE MIDNIGHT FOR EVERYONE: the day is UTC, the Faps chest's rule, stated on the gauge.
 */

const STORAGE_KEY = "gp:wall-allowance";

export const utcDayOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
export const nextUtcMidnight = (ms: number): number => {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1);
};

interface WallAllowanceState {
  /** The UTC day the counts belong to; a tick on a new day resets everything. */
  day: string;
  /** The ledger's wall-seconds at the last sync, or null when nothing has synced (anonymous, or before the first answer). */
  serverSeconds: number | null;
  /** Wall-seconds counted here since the last sync (or since the day began). */
  localSeconds: number;
  /** Advance the meter: `tiles` tiles playing for `dt` seconds. */
  tick: (tiles: number, dt: number, now: number) => void;
  /** The ledger's word: replaces the base and clears what was counted since. */
  sync: (serverSeconds: number, now: number) => void;
  /** Wall-seconds used today, as best this screen knows. */
  used: () => number;
}

function readStored(day: string): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const v = JSON.parse(raw) as { day?: unknown; seconds?: unknown };
    return v.day === day && typeof v.seconds === "number" && v.seconds >= 0 ? v.seconds : 0;
  } catch {
    return 0;
  }
}
function writeStored(day: string, seconds: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ day, seconds }));
  } catch {
    /* blocked storage: the day's count lives only as long as the page */
  }
}

const initialDay = utcDayOf(Date.now());
export const useWallAllowance = create<WallAllowanceState>((set, get) => ({
  day: initialDay,
  serverSeconds: null,
  localSeconds: typeof localStorage !== "undefined" ? readStored(initialDay) : 0,
  tick: (tiles, dt, now) => {
    const day = utcDayOf(now);
    const s = get();
    if (day !== s.day) {
      set({ day, serverSeconds: s.serverSeconds === null ? null : 0, localSeconds: 0 });
      writeStored(day, 0);
      return;
    }
    if (tiles <= 0 || dt <= 0) return;
    const localSeconds = s.localSeconds + tiles * dt;
    set({ localSeconds });
    // Anonymous keeps its own book; a signed-in screen's book is the ledger's, and the
    // stored copy would only ever be stale beside it.
    if (s.serverSeconds === null) writeStored(day, localSeconds);
  },
  sync: (serverSeconds, now) => {
    const day = utcDayOf(now);
    set({ day, serverSeconds: Math.max(0, serverSeconds), localSeconds: 0 });
  },
  used: () => {
    const s = get();
    return (s.serverSeconds ?? 0) + s.localSeconds;
  },
}));

/** Called by the watch batcher with the ledger's answer. */
export function syncWallAllowance(wallSeconds: number): void {
  useWallAllowance.getState().sync(wallSeconds, Date.now());
}

/** Seed from the ledger once a signed-in session is known. Silent on failure: the
 *  local count carries until the first batch answers. */
export async function seedWallAllowance(): Promise<void> {
  if (!useSession.getState().user) return;
  try {
    const r = await userApi.wallAllowance();
    syncWallAllowance(r.wallSeconds);
  } catch {
    /* the local count carries */
  }
}
